// src/components/Timeline/types.ts
import { useMemo } from 'react';
import { isSameDay, isBefore, isAfter } from 'date-fns';

export interface Activity {
   id: string;
   title: string;
   description: string;
   date: Date;
   startTime?: string;
   endTime?: string;
   location: string;
   coordinates: [number, number];
   url?: string;
   tags: string[];
   type?: 'activity' | 'accommodation' | 'transportation' | 'food';
   images?: string[];
}

export interface Comment {
   id: string;
   user: {
      id: string;
      name: string;
      avatar?: string;
   };
   text: string;
   timestamp: Date;
}

export interface Accommodation {
   id: string;
   name: string;
   address: string;
   checkIn?: Date;
   checkOut?: Date;
   coordinates: [number, number];
   images?: string[];
}

export interface Transportation {
   id: string;
   type: 'flight' | 'train' | 'bus' | 'car' | 'ferry' | 'other';
   from: string;
   to: string;
   departureTime?: Date;
   arrivalTime?: Date;
   notes?: string;
   images?: string[];
}

export interface TimelineEvent {
   id: string;
   type: 'activity' | 'accommodation' | 'transportation' | 'milestone';
   date: Date;
   title: string;
   description?: string;
   images?: string[];
   comments?: Comment[];
   reference: Activity | Accommodation | Transportation | null;
}

export interface TripCollaborator {
   id: string;
   name: string;
   avatar?: string;
   role: 'owner' | 'editor' | 'viewer';
}

export interface TimelineViewProps {
   canUserAccess: (role: string[]) => any;
   startDate: Date;
   endDate: Date;
   activities: Activity[];
   accommodations: Accommodation[];
   transportations?: Transportation[];
   events?: TimelineEvent[];
   onEditActivity: (activityId: string) => void;
   onDeleteActivity: (activityId: string) => void;
   onAddComment?: (eventId: string, comment: string) => void;
   view?: 'day' | 'compact' | 'timeline';
   currentUser?: {
      id: string;
      name: string;
      avatar?: string;
   };
   collaborators?: TripCollaborator[];
   tripName?: string;
   tripId?: string;
}

export type TimelineViewType = 'day' | 'compact' | 'timeline';

// Hook implementation
export function useTimelineData(props: TimelineViewProps) {
   const {
      startDate,
      endDate,
      activities,
      accommodations,
      transportations = [],
      events = [],
   } = props;

   // Generate array of days between start and end date
   const days = useMemo(() => {
      const daysArray = [];
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
         daysArray.push(new Date(currentDate));
         currentDate = new Date(currentDate);
         currentDate.setDate(currentDate.getDate() + 1);
      }
      return daysArray;
   }, [startDate, endDate]);

   // Determine if trip is past, current, or future
   const today = new Date();
   const isPastTrip = useMemo(() => isBefore(endDate, today), [endDate]);
   const isFutureTrip = useMemo(() => isAfter(startDate, today), [startDate]);
   const isCurrentTrip = useMemo(() => !isPastTrip && !isFutureTrip, [isPastTrip, isFutureTrip]);

   // Get activities for a specific day
   const getActivitiesForDay = (day: Date) => {
      return activities
         .filter((activity) => isSameDay(new Date(activity.date), day))
         .sort((a, b) => {
            if (!a.startTime && !b.startTime) return 0;
            if (!a.startTime) return -1;
            if (!b.startTime) return 1;
            return a.startTime.localeCompare(b.startTime);
         });
   };

   // Get accommodations active on a specific day
   const getAccommodationsForDay = (day: Date) => {
      return accommodations.filter((accommodation) => {
         if (!accommodation.checkIn || !accommodation.checkOut) return true;

         const date = new Date(day);
         const checkIn = new Date(accommodation.checkIn);
         const checkOut = new Date(accommodation.checkOut);

         return date >= checkIn && date < checkOut;
      });
   };

   // Get transportation for a specific day
   const getTransportationForDay = (day: Date) => {
      return transportations.filter((transportation) => {
         if (!transportation.departureTime) return false;

         const date = new Date(day);
         const departureDate = new Date(transportation.departureTime);

         return isSameDay(date, departureDate);
      });
   };

   // Get events for a specific day
   const getEventsForDay = (day: Date) => {
      return events.filter((event) => isSameDay(new Date(event.date), day));
   };

   // Get trip status
   const getTripStatus = () => {
      if (isPastTrip) {
         return { label: 'Past Trip', color: 'secondary' };
      } else if (isCurrentTrip) {
         return { label: 'Active Trip', color: 'primary' };
      } else {
         return { label: 'Upcoming Trip', color: 'success' };
      }
   };

   return {
      days,
      isPastTrip,
      isFutureTrip,
      isCurrentTrip,
      tripStatus: getTripStatus(),
      getActivitiesForDay,
      getAccommodationsForDay,
      getTransportationForDay,
      getEventsForDay,
   };
}
